import { useCallback } from 'react'

import Cookies from 'js-cookie'
import { useRouter } from 'next/router'

import { BasinEvents } from '@appTypes/BasinEvents.interface'
import {
    CreditCardProductCategory,
    ScoredCreditCardProduct,
} from '@appTypes/EligibilityApi.interface'
import {
    AcquisitionJourneyBaseData,
    AcquisitionJourneyCardsTableClickout,
    AcquisitionJourneyCardsTableViewConsents,
    AcquisitionJourneyCardsTableViewError,
    AcquisitionJourneyCardsTableViewEventDetails,
    AcquisitionJourneyCardsTableViewNameTypes,
    AcquisitionJourneyCardsTableViewOutcome,
    AcquisitionJourneyCardsTableViewQuote,
} from '@appTypes/GoogleTagManager.interface'

import { KbaScenario } from '@config/kbaScenarioKeys'

import { getIsDownsellCreditCard } from '@routes/ScoredTableRoute/components/ScoredTable/utils/getDownsellCreditCards'

import { getAppConfig } from '@utils/getAppConfig'
import { getGtmQueryParamDataFromAppQueryState } from '@utils/getGtmQueryParamDataFromAppQueryState'
import { getMarketingTrackingData } from '@utils/getMarketingTrackingData'

import useAppQueryState from './useAppQueryState'
import { useBasin } from './useBasin'
import useFeatureFlags from './useFeatureFlags'
import useGoogleTagManager from './useGoogleTagManager'
import { getVariationsFromFeatureFlags } from './utils/getVariationsFromFeatureFlags'

type AppPromotionTypes = 'creditCardsTable' | 'improveEligibilityPromotion'

const useLogEvents = () => {
    const appConfig = getAppConfig()
    const { send } = useBasin()
    const { appQueryState } = useAppQueryState()
    const gtmSend = useGoogleTagManager()
    const { featureFlags } = useFeatureFlags()
    const { asPath } = useRouter()
    const queryString = asPath.split('?')[1]

    const trackingData = getMarketingTrackingData(appQueryState)
    const gtmTrackingData = getGtmQueryParamDataFromAppQueryState(appQueryState)
    const acquisitionGtmBaseData: Omit<
        AcquisitionJourneyBaseData,
        'event_ts' | 'event_details'
    > = {
        brand: 'totallymoney',
        category: 'cc',
        cookie_consents: Cookies.get('cookieyes-consent'),
        event: 'data_custom_event',
        meta: {
            ...gtmTrackingData,
            form_landing_url: queryString,
        },
        session_id: Cookies.get('BasinSessionId'),
    }

    const whitelabel = appConfig.NAME_WHITELABEL

    const logAcquisitionCtaClickEvent = useCallback(() => {
        gtmSend({
            ...acquisitionGtmBaseData,
            event_details: {
                journey: 'Credit Cards Signup' as const,
                step: 'considerationPage',
                step_url: window.location.origin + window.location.pathname,
            },
            event_name: 'form_open',
            event_ts: new Date().toISOString(),
        })
    }, [gtmSend, appQueryState])

    // Customer returns to cards table after viewing acquisition page and going through registration process.
    const logAcquisitionJourneyCardsTableViewEvent = useCallback(
        ({
            consents,
            error,
            eventName,
            eventDetails,
            formData,
            outcome,
            quote,
        }: {
            consents: AcquisitionJourneyCardsTableViewConsents
            error?: AcquisitionJourneyCardsTableViewError
            eventDetails: AcquisitionJourneyCardsTableViewEventDetails
            eventName: AcquisitionJourneyCardsTableViewNameTypes
            formData: any
            outcome: AcquisitionJourneyCardsTableViewOutcome
            quote: AcquisitionJourneyCardsTableViewQuote
        }) => {
            gtmSend({
                ...acquisitionGtmBaseData,
                consents,
                error,
                event_details: eventDetails,
                event_name: eventName,
                event_ts: new Date().toISOString(),
                form_data: formData,
                outcome,
                quote,
            })
        },
        [gtmSend, appQueryState]
    )

    const logAcquisitionJourneyCardsTableClickOutEvent = useCallback(
        ({
            consents,
            clickout,
            error,
            eventName,
            eventDetails,
            formData,
            outcome,
            quote,
        }: {
            clickout: AcquisitionJourneyCardsTableClickout
            consents?: AcquisitionJourneyCardsTableViewConsents
            error?: AcquisitionJourneyCardsTableViewError
            eventDetails: AcquisitionJourneyCardsTableViewEventDetails
            eventName: 'form_clickout'
            formData?: any
            outcome: AcquisitionJourneyCardsTableViewOutcome
            quote?: AcquisitionJourneyCardsTableViewQuote
        }) => {
            gtmSend({
                ...acquisitionGtmBaseData,
                clickout,
                consents,
                error,
                event_details: eventDetails,
                event_name: eventName,
                event_ts: new Date().toISOString(),
                form_data: formData,
                outcome,
                quote,
            })
        },
        [gtmSend, appQueryState]
    )

    const logVisitEvent = useCallback(() => {
        const variations = getVariationsFromFeatureFlags(featureFlags)

        const eventData = {
            referrer: document.referrer,
            url: document.URL,
            useragent: navigator.userAgent,
            client: whitelabel,
            partnerName: whitelabel,

            customerId: appQueryState.customerId,
            customerType: appQueryState.customerType,
            isKnownCustomer: appQueryState.isKnownCustomer,
            adgroup: appQueryState.adgroup,
            visitOrigin: appQueryState.visitOrigin,

            sendId: trackingData.sendId,
            mediasource: trackingData.m,
            campaign: trackingData.cam,
            keyword: trackingData.k,
            googleClickId: trackingData.gclid,
            variation: variations.join(','),
        }

        send('Visit', eventData)
    }, [send, appQueryState, trackingData, whitelabel])

    const logPageViewEvent = useCallback(
        ({ category }: { category: string }) => {
            const eventData = {
                category,
                visitOrigin: appQueryState.visitOrigin,
                url: document.URL,
                partnername: whitelabel,
                client: 'web',
                customerId: appQueryState.customerId,
                scoreId: appQueryState.scoreId,
            }
            send('PageView', eventData)
        },
        [send, appQueryState]
    )

    const logCheckEligibilityEvent = useCallback(
        ({
            trigger,
            category,
            productCode,
            rowNumber,
        }: {
            category: string
            productCode?: string
            rowNumber?: number
            trigger: string
        }) => {
            send('FormOpen', {
                trigger,
                category,
                productCode,
                row: rowNumber,
            })
        },
        [send]
    )

    const logNavigateToCardPageEvent = useCallback(
        ({
            rowNumber,
            productCode,
            trigger,
            isNewOffer,
        }: {
            isNewOffer: boolean
            productCode: string
            rowNumber: number
            trigger: string
        }) => {
            gtmSend({
                event: 'show_details',
                trigger,
            })

            send('RowExpand', {
                row: rowNumber,
                productcode: productCode,
                isNewOffer,
            })
        },
        [send]
    )

    const logCardApplyEvent = useCallback(
        ({
            productCode,
            score,
            trigger,
            clickSource,
            rowNumber,
            status,
            category,
            isBestMatchCard,
            isCounterOffer,
            isNewOffer,
        }: {
            category: CreditCardProductCategory
            clickSource: string
            isBestMatchCard: boolean
            isCounterOffer: boolean
            isNewOffer: boolean
            productCode: string
            rowNumber: number
            score: number
            status: string
            trigger: string
        }) => {
            gtmSend({
                event: 'cards-click-out',
                category,
                hasPassword: false,
            })

            send('PostClick', {
                customerId: appQueryState.customerId,
                customerType: appQueryState.customerType,
                isKnownCustomer: appQueryState.isKnownCustomer,
                visitOrigin: appQueryState.visitOrigin,
                score,
                status,
                isBestMatchCard,
                isCounterOffer,
                category,
                row: rowNumber,
                clicksource: clickSource,
                productcode: productCode,
                trigger,
                client: 'web',
                isDownsell: appQueryState.referrer === 'loansdownsell',
                googleClickId: trackingData.gclid,
                isNewOffer,
            })
        },
        [
            appQueryState.customerId,
            appQueryState.customerType,
            appQueryState.isKnownCustomer,
            appQueryState.referrer,
            appQueryState.visitOrigin,
            gtmSend,
            send,
        ]
    )

    const logAlternativeProductApplyEvent = useCallback(
        ({
            productCode,
            rowNumber,
            trigger,
            clickSource,
        }: {
            clickSource: string
            productCode: string
            rowNumber: number
            trigger: string
        }) => {
            send('PostClick', {
                productCode,
                row: rowNumber,
                score: 1,
                trigger,
                clicksource: clickSource,
                client: 'web',
                status: '',
                category: 'AP',
                isBestMatchCard: false,
                googleClickId: trackingData.gclid,
            })
        },
        [send]
    )

    const logSortChangeEvent = useCallback(
        ({ sortBy, category }: { category: string; sortBy: string }) => {
            send('Sort', {
                sortby: sortBy,
                category,
            })
        },
        [send]
    )

    const logCategoryChangeEvent = useCallback(
        ({ category }: { category: string }) => {
            send('CategoryChange', {
                category,
            })
        },
        [send]
    )

    const logAlternativeProductCategoryChangeEvent = useCallback(
        ({ categoryName }: { categoryName: string }) => {
            send('AlternativeProductCategoryClick', {
                customerId: appQueryState.customerId,
                categoryName,
            })
        },
        [appQueryState.customerId, send]
    )

    const logBannerKbaDisplayEvent = useCallback(
        ({ scenario }: { scenario: KbaScenario }) => {
            send('BannerKbaDisplay', {
                customerId: appQueryState.customerId,
                url: document.URL,
                scenario,
            })
        },
        [appQueryState.customerId, send]
    )

    const logBannerKbaClickEvent = useCallback(
        ({ scenario }: { scenario: KbaScenario }) => {
            send('BannerKbaClick', {
                customerId: appQueryState.customerId,
                scenario,
            })
        },
        [appQueryState.customerId, send]
    )

    const logScoredTableUpdateEvent = useCallback(
        ({
            cards,
            minCreditLimit,
            category,
            sortedBy,
            seenNewOffers,
            numberOfNewOffersSeen,
        }: {
            cards: ScoredCreditCardProduct[] | null
            category: CreditCardProductCategory
            minCreditLimit?: number | null
            numberOfNewOffersSeen: number
            seenNewOffers: boolean
            sortedBy: string
        }) => {
            if (cards == null) return
            const categoryCards = cards.map((card: ScoredCreditCardProduct) => {
                return {
                    productCode: card.productCode,
                    isSponsored: card.sponsored,
                    isDownsell: getIsDownsellCreditCard(card),
                    guaranteedCreditLimit:
                        card.guaranteedRate?.guaranteedCreditLimit,
                    guaranteedOfferType:
                        card.guaranteedRate?.guaranteedOfferType,
                    isCounterOffer: card.guaranteedRate?.isCounterOffer,
                }
            })
            send('ScoredTableUpdate', {
                customerId: appQueryState.customerId,
                sortedProducts: categoryCards,
                minCreditLimit,
                category,
                sortedBy,
                seenNewOffers,
                numberOfNewOffersSeen,
            })
        },
        [send]
    )

    const logAppPromotionView = useCallback(
        ({ promotion }: { promotion: AppPromotionTypes }) => {
            send('AppPromotionView', {
                promotion,
                source: 'cardsProductTable',
                platform: navigator.userAgent,
            })
        },
        [send]
    )

    const logAppPromotionClick = useCallback(
        ({
            promotion,
            source,
        }: {
            promotion: AppPromotionTypes
            source:
                | 'cardsProductTable'
                | 'creditReport'
                | 'dismiss'
                | 'getTheApp'
                | 'modal'
        }) => {
            send('AppPromotionClick', {
                promotion,
                source,
                platform: navigator.userAgent,
            })
        },
        [send]
    )

    const logFeatureCardsViewEvent = useCallback(
        ({
            numberOfFeaturedCards,
            category,
        }: {
            category: CreditCardProductCategory
            numberOfFeaturedCards: number
        }) => {
            send('FeatureCards', {
                numberOfFeaturedCards,
                category,
            })
        },
        [send]
    )

    const logCreditLimitIntroView = useCallback(() => {
        send('CreditLimitIntroView', {
            customerId: appQueryState.customerId,
            client: 'web',
        })
    }, [send])

    const logMinCreditLimitSubmit = useCallback(
        ({
            minCreditLimit,
            source,
        }: {
            minCreditLimit: number
            source:
                | 'GuaranteedCreditLimitIntro'
                | 'creditCardsMinCreditLimitUpdateModal'
        }) => {
            send('MinCreditLimitSubmit', {
                customerId: appQueryState.customerId,
                minCreditLimit,
                source,
                client: 'web',
            })
        },
        [send]
    )

    const logMinCreditLimitSkip = useCallback(
        ({ source }: { source: 'GuaranteedCreditLimitIntro' }) => {
            send('MinCreditLimitSkip', {
                customerId: appQueryState.customerId,
                source,
                client: 'web',
            })
        },
        [send]
    )

    const logGuaranteedCreditLimitLanding = useCallback(() => {
        send('GuaranteedCreditLimitLanding', {
            customerId: appQueryState.customerId,
            scoreId: appQueryState.scoreId,
        })
    }, [send])

    const logClickOnNewOffersBanner = useCallback(
        ({ productCode }: BasinEvents['NewOffersBannerClick']) => {
            send('NewOffersBannerClick', { productCode })
        },
        [send]
    )

    return {
        logAcquisitionCtaClickEvent,
        logAcquisitionJourneyCardsTableViewEvent,
        logAcquisitionJourneyCardsTableClickOutEvent,

        logVisitEvent,
        logPageViewEvent,
        logCheckEligibilityEvent,
        logNavigateToCardPageEvent,
        logCardApplyEvent,
        logAlternativeProductApplyEvent,
        logCategoryChangeEvent,
        logSortChangeEvent,
        logAlternativeProductCategoryChangeEvent,
        logFeatureCardsViewEvent,
        logScoredTableUpdateEvent,

        logBannerKbaDisplayEvent,
        logBannerKbaClickEvent,

        logAppPromotionClick,
        logAppPromotionView,

        logCreditLimitIntroView,
        logMinCreditLimitSubmit,
        logMinCreditLimitSkip,
        logGuaranteedCreditLimitLanding,

        logClickOnNewOffersBanner,
    }
}

export default useLogEvents
